import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import api from '../../lib/api';

/**
 * Operational Base on/off for one organization. DEFAULT OFF for every org,
 * including pre-existing ones. When off, that org's users see no Operational
 * Base link anywhere and /operational-base sends them to the Dashboard.
 * Super admins always keep access.
 */
export const OperationalBaseToggle = ({ orgId, disabled = false }) => {
    const { t } = useTranslation();
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(!!orgId);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!orgId) { setLoading(false); return; }
        setLoading(true);
        api.get(`/organizations/${orgId}/operational-base`)
            .then((res) => setEnabled(!!res.data?.operational_base_enabled))
            .catch(() => setEnabled(false))
            .finally(() => setLoading(false));
    }, [orgId]);

    const toggle = async (next) => {
        setSaving(true);
        setEnabled(next);
        try {
            await api.put(`/organizations/${orgId}/operational-base`, { operational_base_enabled: next });
            toast.success(t('organizations.operationalBaseEnabled'));
        } catch (_) {
            setEnabled(!next);
            toast.error(t('common.saveFailed', 'Could not save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex items-start justify-between gap-4 p-3 rounded-md border bg-slate-50/40"
             data-testid="operational-base-toggle-row">
            <div className="flex-1">
                <Label className="text-sm font-semibold flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-500" />
                    {t('organizations.operationalBaseEnabled')}
                </Label>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {t('organizations.operationalBaseEnabledDesc')}
                </p>
            </div>
            {loading
                ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                : <Switch checked={enabled} disabled={disabled || saving}
                          onCheckedChange={toggle}
                          data-testid="operational-base-toggle" />}
        </div>
    );
};

export default OperationalBaseToggle;
